/**
 * TemplateSelectScreen - Template Selection for FMEA Creation Wizard
 * Displays available FMEA templates with preview and selection capabilities
 */

import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { Project } from '@fmeastudio/shared';
import { useWizard } from '../../contexts/WizardContext';
import { getTemplateProjects } from '../../api/projects';
import { TemplateCard } from './TemplateCard';
import { TemplatePreviewModal } from './TemplatePreviewModal';
import './TemplateSelectScreen.css';

export function TemplateSelectScreen() {
  const { goToStep, updateProjectData, setTemplateId } = useWizard();
  const [selectedTemplate, setSelectedTemplate] = useState<Project | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  // Fetch templates using React Query
  const {
    data: templates,
    isLoading,
    isError,
    error,
  } = useQuery<Project[], Error>({
    queryKey: ['templates'],
    queryFn: getTemplateProjects,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const handleTemplateSelect = (template: Project) => {
    setSelectedTemplate(template);
    setShowPreview(true);
  };

  const handleTemplateConfirm = (template: Project) => {
    // Update wizard context with template selection
    updateProjectData({
      name: `${template.name} - Copy`,
      description: template.description || undefined,
      industry: template.industry || undefined,
    });

    // Store template ID in wizard state
    setTemplateId(template.id);

    // Navigate to review step
    goToStep('review');
  };

  const handleGoBack = () => {
    goToStep('welcome');
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="template-select-screen" data-testid="wizard-template-select">
        <div className="template-loading">
          <div className="spinner" />
          <p>Loading templates...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (isError) {
    return (
      <div className="template-select-screen" data-testid="wizard-template-select">
        <div className="template-error">
          <h2>Error Loading Templates</h2>
          <p>{error?.message || 'Failed to load templates'}</p>
          <button onClick={() => goToStep('welcome')} className="btn-secondary">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Empty state
  if (!templates || templates.length === 0) {
    return (
      <div className="template-select-screen" data-testid="wizard-template-select">
        <div className="template-empty">
          <h2>No Templates Available</h2>
          <p>There are no FMEA templates available yet.</p>
          <p>Try creating a project from scratch or importing a BOM instead.</p>
          <button onClick={handleGoBack} className="btn-secondary">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  // Template grid
  return (
    <div className="template-select-screen" data-testid="wizard-template-select">
      <div className="template-header">
        <button onClick={handleGoBack} className="btn-back" aria-label="Go back">
          ← Back
        </button>
        <div className="template-title">
          <h2>Select a Template</h2>
          <p>Choose from pre-built FMEA templates to get started quickly</p>
        </div>
      </div>

      <div className="template-grid">
        {templates && templates.length > 0 ? (
          templates.map((template) => (
            <TemplateCard
              key={template.id}
              template={template}
              onSelect={() => handleTemplateSelect(template)}
            />
          ))
        ) : (
          <div className="template-empty">
            <p>No templates available. Start with a blank project instead.</p>
          </div>
        )}
      </div>

      {/* Template preview modal */}
      {showPreview && selectedTemplate && (
        <TemplatePreviewModal
          template={selectedTemplate}
          onConfirm={() => handleTemplateConfirm(selectedTemplate)}
          onCancel={() => setShowPreview(false)}
        />
      )}
    </div>
  );
}
